// pages/risk/RiskView.jsx — SOUND-473 (DEV)
// Portfolio risk monitoring over the rebuilt presentation.mart_risk (VAMP +
// ECM/HECM per sound_mid_id × month). Backend: GET /risk/* (SOUND-472).
//
// The mart is precomputed + program-centric: standings, headroom and thresholds
// are baked in — the UI only formats them. The mart speaks BPS throughout; the UI
// renders VAMP and chargeback ratios as PERCENT and ECM/HECM as BPS, matching how
// each program states its own thresholds (Visa: 0.65% / 1.5%; MC: 100 / 150 bps).
// Nothing but the formatter changes — sorting, headroom and standings stay on the
// raw bps values. A MID can be RATIO-hot
// (negative headroom) yet still `ok` because Visa/MC also gate on a monthly count
// floor (1,500 / 100-300); we surface that with headroom, not just the standing.
// low_sample dims a ratio whose denominator is too thin to trust.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, ResponsiveContainer,
} from "recharts";
import clsx from "clsx";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { isProcessor } from "utilities/userType";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import Skeleton from "components/Skeleton";
import {
  ShieldAlert, ShieldCheck, Flame, AlertTriangle, Link2, Activity, Gauge, CreditCard,
} from "lucide-react";

// ─── formatting ───
const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
const bps = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })} bps`);
// Same mart value, percent-rendered: 1 bps = 0.01%, so 2 dp holds the mart's full
// precision to the nearest basis point (65 bps → 0.65%).
const pct = (v) => (v === null || v === undefined ? "—" : `${(Number(v) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);
// Axis ticks — no forced decimals, so 0 / 50 / 100 / 150 read 0% / 0.5% / 1% / 1.5%.
const pctTick = (v) => `${(Number(v) / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;

function cssVar(name, alpha = 1) {
  if (typeof window === "undefined") return "";
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return "";
  const [h, s, l] = raw.split(/\s+/);
  return `hsla(${parseFloat(h)}, ${s}, ${l}, ${alpha})`;
}
const tooltipStyle = () => ({
  background: cssVar("--card", 0.98), border: `1px solid ${cssVar("--border", 1)}`,
  borderRadius: 8, fontSize: 12,
});

const CHIP = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1";

// ─── "over program" predicates ───
// Hoisted so the chips, the colored ratio figures and the Over VAMP / Over ECM
// filter all read from ONE definition of "over" — a filter that disagreed with
// the badge next to it would be worse than no filter.
const isMonitoringVamp = (r) => r.vamp_standing === "monitoring_only" || r.visa_settled === 0;
// Ratio-hot: past the ratio gate but possibly still `ok` because the monthly count
// floor hasn't been cleared. Low-sample rows are excluded — too noisy to act on.
const isRatioHotVamp = (r) => !r.vamp_low_sample && r.vamp_headroom_bps < 0;
const isOverVamp = (r) => !isMonitoringVamp(r) && (r.vamp_standing === "excessive_merchant" || isRatioHotVamp(r));
// ECM mirrors VAMP. This matters more than it looks: MC's ECM tier also needs the
// 100-chargeback monthly floor, which nothing in this portfolio clears, so a
// tier-only definition of "over ECM" is dead on arrival — every MC-hot MID sits at
// tier `ok` with a 2-4% ratio. The ratio gate is the signal worth acting on.
const isMonitoringEcm = (r) => r.ecm_tier === "monitoring_only" || r.mc_sales_prior == null;
const isRatioHotEcm = (r) => !r.ecm_low_sample && r.ecm_headroom_bps < 0;
const isOverEcm = (r) => !isMonitoringEcm(r) && (r.ecm_tier === "ECM" || r.ecm_tier === "HECM" || isRatioHotEcm(r));

// VAMP standing → chip. excessive is the only true breach; ratio-hot (negative
// headroom, count-gated) renders as a warning so it doesn't hide behind `ok`.
function VampBadge({ row }) {
  if (isMonitoringVamp(row))
    return <span className={clsx(CHIP, "bg-muted text-muted-foreground ring-border")}>Monitoring</span>;
  if (row.vamp_standing === "excessive_merchant")
    return <span className={clsx(CHIP, "bg-destructive/15 text-destructive ring-destructive/30")}><Flame className="h-3 w-3" /> Excessive</span>;
  if (isRatioHotVamp(row))
    return <span className={clsx(CHIP, "bg-warning/15 text-warning ring-warning/30")}><AlertTriangle className="h-3 w-3" /> Over ratio</span>;
  return <span className={clsx(CHIP, "bg-success/15 text-success ring-success/30")}><ShieldCheck className="h-3 w-3" /> OK</span>;
}

// Same precedence as VampBadge: monitoring, then the real tiers, then ratio-hot as
// a warning so a 4% MC ratio can't hide behind `ok` just because the count floor
// hasn't been cleared.
function EcmBadge({ row }) {
  if (isMonitoringEcm(row))
    return <span className={clsx(CHIP, "bg-muted text-muted-foreground ring-border")}>Monitoring</span>;
  if (row.ecm_tier === "HECM")
    return <span className={clsx(CHIP, "bg-destructive/15 text-destructive ring-destructive/30")}><Flame className="h-3 w-3" /> HECM</span>;
  if (row.ecm_tier === "ECM")
    return <span className={clsx(CHIP, "bg-warning/15 text-warning ring-warning/30")}><AlertTriangle className="h-3 w-3" /> ECM</span>;
  if (isRatioHotEcm(row))
    return <span className={clsx(CHIP, "bg-warning/15 text-warning ring-warning/30")}><AlertTriangle className="h-3 w-3" /> Over ratio</span>;
  return <span className={clsx(CHIP, "bg-success/15 text-success ring-success/30")}><ShieldCheck className="h-3 w-3" /> OK</span>;
}

// Headroom meter — current ratio vs the program threshold. Fill = how much of the
// threshold is consumed; over-threshold overflows red. Math is always bps; `fmt`
// only decides the unit the gap reads in (percent for VAMP, bps for ECM/HECM).
function HeadroomMeter({ current, threshold, lowSample, fmt = bps }) {
  if (lowSample || current == null || !threshold) return <div className="text-[11px] text-muted-foreground">low sample</div>;
  const frac = Math.min(current / threshold, 1.35);
  const over = current > threshold;
  const pct = Math.min(frac, 1) * 100;
  const overPct = over ? Math.min((frac - 1) * 100, 35) : 0;
  return (
    <div className="mt-1">
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden flex">
        <div className={clsx("h-full", over ? "bg-warning" : "bg-brand")} style={{ width: `${pct}%` }} />
        {over && <div className="h-full bg-destructive" style={{ width: `${overPct}%` }} />}
      </div>
      <div className={clsx("mt-0.5 text-[11px]", over ? "text-warning" : "text-muted-foreground")}>
        {over ? `${fmt(current - threshold)} over ${fmt(threshold)}` : `${fmt(threshold - current)} to ${fmt(threshold)} limit`}
      </div>
    </div>
  );
}

function AlertPips({ row }) {
  const items = [["TC40", row.tc40_count], ["Eth", row.ethoca_count], ["CDRN", row.cdrn_count], ["RDR", row.rdr_count]];
  return (
    <div className="flex flex-wrap gap-1">
      {items.map(([label, n]) => (
        <span key={label} className={clsx("inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]",
          n > 0 ? "bg-muted text-foreground" : "bg-transparent text-muted-foreground/50")}>
          {label}<span className="font-semibold">{fmtInt(n)}</span>
        </span>
      ))}
    </div>
  );
}

// Big ratio figure — colored by severity, dimmed when low-sample.
function RatioFigure({ value, lowSample, over, fmt = bps }) {
  if (lowSample) return <div className="text-sm text-muted-foreground">— <span className="text-[11px]">low</span></div>;
  return <div className={clsx("text-lg font-semibold tabular-nums", over ? "text-warning" : "text-foreground")}>{fmt(value)}</div>;
}

function midIdentity(r) {
  const primary = r.mid_name || r.descriptor || r.sound_mid_id?.slice(0, 8) || "—";
  const bits = [r.descriptor && r.descriptor !== r.mid_name ? r.descriptor : null, r.processor_name].filter(Boolean);
  return { primary, secondary: bits.join(" · "), acquirer: r.mid_number };
}

// ─── detail / trend modal ───
function MidDetailModal({ auth, companyId, row, onClose }) {
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await makeApiRequest("get", apiRoutes.riskMidTrend(row.sound_mid_id), auth, companyId);
        const series = (res?.data?.data || []).map((d) => ({
          month: String(d.month).slice(0, 7),
          vampBps: d.vamp_bps, ecmBps: d.ecm_bps, settled: d.total_settled,
        }));
        if (!cancelled) setTrend(series);
      } catch { /* leave empty */ } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [row.sound_mid_id, auth, companyId]);

  const id = midIdentity(row);
  const brands = [
    ["Visa", row.visa_settled, row.visa_chargebacks],
    ["Mastercard", row.mc_settled, row.mc_chargebacks],
    ["Amex", row.amex_settled, row.amex_chargebacks],
    ["Discover", row.discover_settled, row.discover_chargebacks],
  ];
  const Block = ({ icon, title, children }) => (
    <div className="surface rounded-xl ring-1 ring-border p-4">
      <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-foreground">{icon}{title}</div>
      {children}
    </div>
  );
  const Stat = ({ label, value, tone }) => (
    <div><div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={clsx("text-base font-semibold tabular-nums", tone || "text-foreground")}>{value}</div></div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative surface rounded-xl ring-1 ring-border p-5 w-full max-w-3xl max-h-[88vh] overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-brand" />
            <div>
              <h3 className="text-base font-semibold text-foreground">{id.primary}</h3>
              <div className="text-xs text-muted-foreground">{[id.secondary, id.acquirer && `MID ${id.acquirer}`].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm -mt-1 -mr-1" aria-label="Close" onClick={onClose}>✕</button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Block icon={<Gauge className="h-4 w-4 text-brand" />} title="VAMP (Visa)">
            <div className="flex items-center justify-between">
              <RatioFigure value={row.vamp_bps} lowSample={row.vamp_low_sample} over={isRatioHotVamp(row)} fmt={pct} />
              <VampBadge row={row} />
            </div>
            <HeadroomMeter current={row.vamp_bps} threshold={row.vamp_threshold_bps} lowSample={row.vamp_low_sample} fmt={pct} />
            <div className="mt-2 grid grid-cols-3 gap-2">
              <Stat label="Events" value={fmtInt(row.vamp_event_count)} />
              <Stat label="Visa settled" value={fmtInt(row.visa_settled)} />
              <Stat label="Count floor" value={fmtInt(row.vamp_threshold_count)} />
            </div>
          </Block>
          <Block icon={<Gauge className="h-4 w-4 text-brand" />} title="ECM / HECM (Mastercard)">
            <div className="flex items-center justify-between">
              <RatioFigure value={row.ecm_bps} lowSample={row.ecm_low_sample} over={isOverEcm(row)} />
              <EcmBadge row={row} />
            </div>
            <HeadroomMeter current={row.ecm_bps} threshold={row.ecm_threshold_bps} lowSample={row.ecm_low_sample} />
            <div className="mt-2 grid grid-cols-3 gap-2">
              <Stat label="MC first-CBs" value={fmtInt(row.mc_chargebacks)} />
              <Stat label="Prior-mo sales" value={row.mc_sales_prior == null ? "—" : fmtInt(row.mc_sales_prior)} />
              <Stat label="ECM floor" value={fmtInt(row.ecm_threshold_count)} />
            </div>
          </Block>
        </div>

        <Block icon={<CreditCard className="h-4 w-4 text-brand" />} title="By card brand">
          <table className="w-full text-sm">
            <thead><tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="text-left font-medium py-1">Brand</th><th className="text-right font-medium">Settled</th><th className="text-right font-medium">Chargebacks</th><th className="text-right font-medium">CB %</th></tr></thead>
            <tbody className="divide-y divide-border">
              {brands.map(([b, settled, cb]) => (
                <tr key={b}><td className="py-1.5 text-foreground">{b}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtInt(settled)}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtInt(cb)}</td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">{settled > 0 ? pct((10000 * cb) / settled) : "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </Block>

        <div className="mt-3">
          {/* Both ratios are bps in the mart, so they plot on one shared scale; only the
              labels differ (axis + VAMP in %, ECM in bps in its tooltip). */}
          <div className="text-xs text-muted-foreground mb-2">Program trend — VAMP &amp; ECM vs settled volume, on a shared ratio scale (1% = 100 bps). Dashed line = the 1.5% / 150 bps program limit.</div>
          {loading ? <Skeleton.Rect className="h-64 w-full" /> : trend.length === 0 ? (
            <p className="text-sm text-muted-foreground">No trend data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={trend} margin={{ top: 8, right: 12, bottom: 0, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--border", 0.5)} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="bps" tick={{ fontSize: 11 }} width={52} tickFormatter={pctTick} />
                <YAxis yAxisId="vol" orientation="right" tick={{ fontSize: 11 }} width={52} tickFormatter={(v) => fmtInt(v)} />
                <Tooltip contentStyle={tooltipStyle()} formatter={(v, n) => (n === "Settled" ? [fmtInt(v), n] : n === "VAMP" ? [pct(v), n] : [bps(v), n])} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine yAxisId="bps" y={150} stroke={cssVar("--warning", 0.7)} strokeDasharray="4 4" />
                <Bar yAxisId="vol" dataKey="settled" name="Settled" fill={cssVar("--muted-foreground", 0.22)} isAnimationActive={false} />
                <Line yAxisId="bps" type="monotone" dataKey="vampBps" name="VAMP" stroke={cssVar("--destructive", 1)} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
                <Line yAxisId="bps" type="monotone" dataKey="ecmBps" name="ECM" stroke={cssVar("--warning", 1)} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── main ───
const SORTABLE = { total_settled: 1, vamp_bps: 1, ecm_bps: 1, total_chargebacks: 1 };

function RiskViewInner() {
  const { user } = useAuth();
  // Processors (SOUND-563) see this as their scoped monitoring home. Hide the
  // internal-ops affordances (MID-linking worklist) and the cross-entity
  // Processor/Partner filters that don't apply to a single, server-pinned
  // processor. Data itself is scoped server-side.
  const isProcessorUser = isProcessor(user);
  const effectiveCompanyId = useEffectiveCompanyId();
  const auth = useMemo(() => ({ id: user?.id, accessToken: user?.accessToken }), [user]);

  const [filters, setFilters] = useState({ months: [], processors: [], companies: [], partners: [], parents: [] });
  const [month, setMonth] = useState("");
  const [processor, setProcessor] = useState("");
  // "Over program" is a client-side cut: the two options below are composites of
  // standing + headroom + the low-sample display guard (the same thing the chips
  // read), not a single mart enum, and /risk/mids returns the whole month
  // unpaginated — so filtering here is both exact and instant. "" = all MIDs.
  const [overProgram, setOverProgram] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [partnerId, setPartnerId] = useState("");
  // SOUND-614: parent filter — internal only, like Partner.
  const [parentId, setParentId] = useState("");
  const [search, setSearch] = useState("");

  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [worklist, setWorklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sort, setSort] = useState({ key: "vamp_bps", dir: "desc" });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user || !effectiveCompanyId) return;
    (async () => {
      try {
        const res = await makeApiRequest("get", apiRoutes.riskFilters, auth, effectiveCompanyId);
        const f = res?.data || {};
        setFilters({ months: f.months || [], processors: f.processors || [], companies: f.companies || [], partners: f.partners || [], parents: f.parents || [] });
        // Default to the latest available month (the CURRENT month) rather than the
        // last COMPLETE month. CPO request; applies to all account types.
        if (f.months?.length) setMonth((m) => m || f.months[0]);
      } catch { /* non-fatal */ }
    })();
  }, [user, effectiveCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!user || !effectiveCompanyId) return;
    setLoading(true); setError(false);
    const p = new URLSearchParams();
    if (month) p.set("month", month);
    if (processor) p.set("processor", processor);
    if (companyId) p.set("company_id", companyId);
    if (partnerId) p.set("partner_id", partnerId);
    if (parentId) p.set("parent_id", parentId);
    if (search) p.set("search", search);
    const qs = p.toString();
    try {
      const [mids, wl, sm] = await Promise.all([
        makeApiRequest("get", `${apiRoutes.riskMids}?${qs}`, auth, effectiveCompanyId),
        // /risk/unresolved is the internal-only MID-linking worklist — a processor
        // 403s there (and the panel is hidden for them), so skip the call entirely.
        // Without this the 403 rejects the whole Promise.all and errors the page.
        isProcessorUser
          ? Promise.resolve(null)
          : makeApiRequest("get", `${apiRoutes.riskUnresolved}?${month ? `month=${month}` : ""}`, auth, effectiveCompanyId),
        makeApiRequest("get", `${apiRoutes.riskSummary}?${month ? `month=${month}` : ""}`, auth, effectiveCompanyId),
      ]);
      setRows(mids?.data?.data || []);
      setWorklist(wl?.data?.data || []);
      setSummary(sm?.data?.summary || {});
    } catch { setError(true); } finally { setLoading(false); }
  }, [user, effectiveCompanyId, month, processor, companyId, partnerId, parentId, search, auth, isProcessorUser]);

  useEffect(() => { load(); }, [load]);

  const sortedRows = useMemo(() => {
    const overPred = overProgram === "vamp" ? isOverVamp : overProgram === "ecm" ? isOverEcm : null;
    const r = overPred ? rows.filter(overPred) : [...rows];
    const { key, dir } = sort;
    r.sort((a, b) => {
      const av = a[key] ?? -Infinity, bv = b[key] ?? -Infinity;
      return dir === "desc" ? bv - av : av - bv;
    });
    return r;
  }, [rows, sort, overProgram]);

  const toggleSort = (key) => {
    if (!SORTABLE[key]) return;
    setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  };
  const arrow = (key) => (sort.key === key ? (sort.dir === "desc" ? " ↓" : " ↑") : "");

  const th = "px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider";
  const thNum = th + " text-right cursor-pointer select-none";

  return (
    <div>
      <PageHeader
        title="Risk"
        subtitle={
          isProcessorUser
            ? "Program monitoring — Visa VAMP, Mastercard ECM/HECM, and chargeback-prevention alerts across the MIDs you acquire."
            : "Portfolio program monitoring — Visa VAMP, Mastercard ECM/HECM, and chargeback-prevention alerts, per MID (DEV)."
        }
        meta={[{ label: "Month", value: month ? month.slice(0, 7) : "—" }, { label: "MIDs monitored", value: fmtInt(summary.mids_monitored) }]}
      />

      <div className="container py-6 space-y-6">
        {/* KPI strip */}
        {loading && !rows.length ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => <div key={i} className="surface rounded-xl ring-1 ring-border p-5"><Skeleton.Rect className="h-5 w-20" /><Skeleton.Rect className="h-3 w-12 mt-2" /></div>)}
          </div>
        ) : (
          <PageKpis
            columns={{ base: 2, md: isProcessorUser ? 3 : 4, lg: isProcessorUser ? 3 : 4 }}
            items={[
              { label: "MIDs monitored", value: fmtInt(summary.mids_monitored), icon: <Activity />, tone: "brand" },
              // These two count MIDs over the program RATIO gate — the same population
              // the chips flag and the Over VAMP / Over ECM filter selects. They used to
              // count `excessive_merchant` / ECM+HECM standings, which additionally
              // require the monthly count floor and so read 0 in every month on record.
              // Tone escalates to destructive only on a true breach.
              {
                label: "Over VAMP", value: fmtInt(summary.vamp_over_ratio), icon: <Flame />,
                tone: summary.vamp_excessive ? "destructive" : summary.vamp_over_ratio ? "warning" : "muted",
              },
              {
                label: "Over ECM", value: fmtInt(summary.ecm_over_ratio), icon: <AlertTriangle />,
                tone: summary.hecm_count ? "destructive" : summary.ecm_over_ratio ? "warning" : "muted",
              },
              // "Unlinked w/ events" is an internal MID-linking metric — omit for processors.
              ...(isProcessorUser ? [] : [{ label: "Unlinked w/ events", value: fmtInt(summary.unresolved_worklist), icon: <Link2 />, tone: "warning" }]),
            ]}
          />
        )}

        {/* Controls */}
        <div className="surface rounded-xl ring-1 ring-border p-4 flex flex-col lg:flex-row lg:items-end gap-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Month</label>
            <select className="select" value={month} onChange={(e) => setMonth(e.target.value)}>
              {filters.months.map((m) => <option key={m} value={m}>{m.slice(0, 7)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Client</label>
            <select className="select max-w-[12rem]" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">All clients</option>
              {filters.companies.map((c) => <option key={c.companyId} value={c.companyId}>{c.companyName}</option>)}
            </select>
          </div>
          {/* SOUND-614: "see all MIDs for one parent company". 32 companies have
              a parent and hold 82 of 256 MIDs, so without this a third of the
              portfolio can only be reviewed one child at a time.
              Internal only — parent/child structure is not something a processor
              should learn, same shielding as Partner (SOUND-570). */}
          {!isProcessorUser && (
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Parent</label>
              <select className="select max-w-[12rem]" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                <option value="">All parents</option>
                {filters.parents.map((p) => <option key={p.parentId} value={p.parentId}>{p.parentName}</option>)}
              </select>
            </div>
          )}
          {!isProcessorUser && (
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Partner</label>
              <select className="select max-w-[12rem]" value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
                <option value="">All partners</option>
                {filters.partners.map((p) => <option key={p.partnerId} value={p.partnerId}>{p.partnerName}</option>)}
              </select>
            </div>
          )}
          {!isProcessorUser && (
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Processor</label>
              <select className="select" value={processor} onChange={(e) => setProcessor(e.target.value)}>
                <option value="">All processors</option>
                {filters.processors.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Program status</label>
            <select className="select" value={overProgram} onChange={(e) => setOverProgram(e.target.value)}>
              <option value="">All MIDs</option>
              <option value="vamp">Over VAMP</option>
              <option value="ecm">Over ECM</option>
            </select>
          </div>
          <div className="lg:ml-auto">
            <label className="text-xs text-muted-foreground block mb-1">Search</label>
            <input className="input" placeholder="MID name / descriptor / number" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        {error && <div className="surface rounded-xl ring-1 ring-border p-8 text-center text-sm text-destructive">Failed to load risk data.</div>}

        {/* MID portfolio table */}
        {!loading && (
          <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">MID portfolio</h3>
              {/* Show the denominator when the over-program cut is on, so a short list
                  reads as "filtered", not as a thin portfolio. */}
              <span className="text-xs text-muted-foreground">
                {overProgram ? `${fmtInt(sortedRows.length)} of ${fmtInt(rows.length)}` : fmtInt(sortedRows.length)} linked MIDs · click a row for detail
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-card">
                  <tr>
                    <th className={th}>MID</th>
                    <th className={thNum} onClick={() => toggleSort("total_settled")}>Settled{arrow("total_settled")}</th>
                    <th className={thNum} onClick={() => toggleSort("vamp_bps")}>VAMP{arrow("vamp_bps")}</th>
                    <th className={thNum} onClick={() => toggleSort("ecm_bps")}>ECM / HECM{arrow("ecm_bps")}</th>
                    <th className={thNum} onClick={() => toggleSort("total_chargebacks")}>Chargebacks{arrow("total_chargebacks")}</th>
                    <th className={th}>Alerts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedRows.map((r) => {
                    const id = midIdentity(r);
                    return (
                      <tr key={r.sound_mid_id} className="cursor-pointer transition-colors hover:bg-muted/40" onClick={() => setSelected(r)}>
                        <td className="px-4 py-3 align-top">
                          <div className="text-sm font-medium text-foreground">{id.primary}</div>
                          {id.secondary && <div className="text-xs text-muted-foreground">{id.secondary}</div>}
                          {id.acquirer && <div className="text-[11px] text-muted-foreground/70">MID {id.acquirer}</div>}
                        </td>
                        <td className="px-4 py-3 text-right align-top tabular-nums text-foreground">{fmtInt(r.total_settled)}</td>
                        <td className="px-4 py-3 text-right align-top w-44">
                          <div className="flex items-center justify-end gap-2">
                            <RatioFigure value={r.vamp_bps} lowSample={r.vamp_low_sample} over={isRatioHotVamp(r)} fmt={pct} />
                            <VampBadge row={r} />
                          </div>
                          <HeadroomMeter current={r.vamp_bps} threshold={r.vamp_threshold_bps} lowSample={r.vamp_low_sample} fmt={pct} />
                        </td>
                        <td className="px-4 py-3 text-right align-top w-44">
                          <div className="flex items-center justify-end gap-2">
                            <RatioFigure value={r.ecm_bps} lowSample={r.ecm_low_sample} over={isOverEcm(r)} />
                            <EcmBadge row={r} />
                          </div>
                          <HeadroomMeter current={r.ecm_bps} threshold={r.ecm_threshold_bps} lowSample={r.ecm_low_sample} />
                        </td>
                        <td className="px-4 py-3 text-right align-top tabular-nums">
                          <div className="text-foreground">{fmtInt(r.total_chargebacks)}</div>
                          <div className="text-[11px] text-muted-foreground">{r.total_settled > 0 ? pct(r.blended_cb_bps) : "—"}</div>
                        </td>
                        <td className="px-4 py-3 align-top"><AlertPips row={r} /></td>
                      </tr>
                    );
                  })}
                  {sortedRows.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">No linked MIDs match the current filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Linking worklist — internal CRM MID-linking action, hidden for processors. */}
        {!loading && !isProcessorUser && worklist.length > 0 && (
          <div className="surface rounded-xl ring-1 ring-warning/30 overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between bg-warning/5">
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-warning" />
                <h3 className="text-sm font-semibold text-foreground">Linking worklist</h3>
              </div>
              <span className="text-xs text-muted-foreground">Unlinked MIDs carrying risk events — link in the CRM to attribute their ratios</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-card">
                  <tr>
                    <th className={th}>External MID</th>
                    <th className={th + " text-right"}>Settled</th>
                    <th className={th + " text-right"}>Chargebacks</th>
                    <th className={th}>Alerts</th>
                    <th className={th}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {worklist.map((w) => (
                    <tr key={w.sound_mid_id} className="hover:bg-muted/30">
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground ring-1 ring-border rounded px-1 py-0.5">{w.provider || "unresolved"}</span>
                          <span className="font-mono text-xs text-foreground">{w.native_key}</span>
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{fmtInt(w.total_settled)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{fmtInt(w.total_chargebacks)}</td>
                      <td className="px-4 py-2.5"><AlertPips row={w} /></td>
                      <td className="px-4 py-2.5 text-right">
                        <a href="/mids" className="btn btn-ghost btn-sm text-brand" onClick={(e) => e.stopPropagation()}>Link →</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {selected && <MidDetailModal auth={auth} companyId={effectiveCompanyId} row={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

export default requireAuth(RiskViewInner);
